/**
 * Tax Dashboard Data Service
 *
 * Connects /TaxDashboard to the real backend API while preserving the shape
 * consumed by the existing dashboard components.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';
const API_STATUS_SUCCESS = 0;

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Parse date from DD/MM/YYYY format to YYYY-MM-DD
const parseDateString = (dateStr) => {
  const match = dateStr.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!match) return null;
  const day = match[1].padStart(2, '0');
  const month = match[2].padStart(2, '0');
  const year = match[3];
  return `${year}-${month}-${day}`;
};

const todayKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const unwrapResponseData = (response) => {
  const body = response && response.data;

  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = body.status ?? body.Status;
    if (status === API_STATUS_SUCCESS) {
      return body.data ?? body.Data ?? {};
    }

    throw new Error(body.message || body.Message || 'Tax dashboard request failed');
  }

  return body || {};
};

const valueOf = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return undefined;
};

const numberOf = (obj, ...keys) => Number(valueOf(obj, ...keys) || 0);

const arrayOf = (value) => (Array.isArray(value) ? value : []);

const normalizePie = (pie) => ({
  labels: arrayOf(valueOf(pie, 'labels', 'Labels')),
  series: arrayOf(valueOf(pie, 'series', 'Series')),
});

// Pending fields keep null (not 0): a null means the state has no pending notion and renders as "-".
const normalizeFunnelStage = (item) => {
  const pendingForms = valueOf(item, 'pendingForms', 'PendingForms');
  const pendingUniqueUsers = valueOf(item, 'pendingUniqueUsers', 'PendingUniqueUsers');

  return {
    stage: valueOf(item, 'stage', 'Stage') || '',
    completeForms: numberOf(item, 'completeForms', 'CompleteForms'),
    completeUniqueUsers: numberOf(item, 'completeUniqueUsers', 'CompleteUniqueUsers'),
    pendingForms: pendingForms === undefined ? null : Number(pendingForms) || 0,
    pendingUniqueUsers: pendingUniqueUsers === undefined ? null : Number(pendingUniqueUsers) || 0,
  };
};

const normalizeTaxSavings = (item) => {
  const group = valueOf(item, 'group', 'Group') || {};
  const avgGap = valueOf(item, 'avg_gap', 'avgGap', 'AvgGap') || {};
  const ctr = valueOf(item, 'ctr', 'Ctr') || {};

  return {
    group: {
      name: valueOf(group, 'name', 'Name') || '',
      cap: numberOf(group, 'cap', 'Cap'),
    },
    avg_gap: {
      deductible_users: numberOf(avgGap, 'deductible_users', 'deductibleUsers', 'DeductibleUsers'),
      deductible_amount: numberOf(avgGap, 'deductible_amount', 'deductibleAmount', 'DeductibleAmount'),
    },
    max_gap: numberOf(item, 'max_gap', 'maxGap', 'MaxGap'),
    ctr: {
      clicks: numberOf(ctr, 'clicks', 'Clicks'),
      recommendation_amount: numberOf(ctr, 'recommendation_amount', 'recommendationAmount', 'RecommendationAmount'),
    },
    products: arrayOf(valueOf(item, 'products', 'Products')),
  };
};

const normalizeSatisfaction = (data) => {
  const reasons = arrayOf(valueOf(data, 'dislikeReasons', 'DislikeReasons')).map((item) => ({
    code: valueOf(item, 'code', 'Code') || '',
    label: valueOf(item, 'label', 'Label', 'reason', 'Reason') || '',
    reason: valueOf(item, 'label', 'Label', 'reason', 'Reason') || '',
    count: numberOf(item, 'count', 'Count'),
  }));

  return {
    like: numberOf(data, 'like', 'Like'),
    dislike: numberOf(data, 'dislike', 'Dislike'),
    likePercentage: numberOf(data, 'likePercentage', 'LikePercentage'),
    dislikePercentage: numberOf(data, 'dislikePercentage', 'DislikePercentage'),
    dislikeReasons: reasons,
  };
};

const normalizeProductCtr = (item) => ({
  product: valueOf(item, 'product', 'Product') || '',
  productType: valueOf(item, 'productType', 'ProductType') || '',
  clicks: numberOf(item, 'clicks', 'Clicks'),
  recommendations: numberOf(item, 'recommendations', 'Recommendations'),
  ctr: numberOf(item, 'ctr', 'Ctr'),
});

const normalizeCustomerIncome = (item) => ({
  totalUsers: numberOf(item, 'totalUsers', 'TotalUsers'),
  ageLessThan30: numberOf(item, 'ageLessThan30', 'AgeLessThan30'),
  age30to44: numberOf(item, 'age30to44', 'age30To44', 'Age30To44'),
  age45to54: numberOf(item, 'age45to54', 'age45To54', 'Age45To54'),
  ageGreaterEqual55: numberOf(item, 'ageGreaterEqual55', 'AgeGreaterEqual55'),
});

const normalizeDailyTrend = (item) => ({
  date: valueOf(item, 'date', 'Date') || '',
  usersVisit: numberOf(item, 'usersVisit', 'UsersVisit'),
  taxFormsCreated: numberOf(item, 'taxFormsCreated', 'TaxFormsCreated'),
  taxCalculated: numberOf(item, 'taxCalculated', 'TaxCalculated'),
  completionPercentage: numberOf(item, 'completionPercentage', 'CompletionPercentage'),
});

const normalizeLiveData = (data) => {
  const overview = valueOf(data, 'overview', 'Overview') || {};
  const consentOverview = valueOf(data, 'consentOverview', 'ConsentOverview') || {};
  const satisfactionData = valueOf(data, 'satisfactionData', 'SatisfactionData') || {};

  return {
    overview: {
      totalUsersVisit: numberOf(overview, 'totalUsersVisit', 'TotalUsersVisit'),
      totalCreatedTaxForms: numberOf(overview, 'totalCreatedTaxForms', 'TotalCreatedTaxForms'),
      completedForms: numberOf(overview, 'completedForms', 'CompletedForms'),
      newUserVisit: numberOf(overview, 'newUserVisit', 'NewUserVisit'),
      totalProductsClick: numberOf(overview, 'totalProductsClick', 'TotalProductsClick'),
      totalRecommendationLike: numberOf(overview, 'totalRecommendationLike', 'TotalRecommendationLike'),
    },
    consentOverview: {
      totalVisitUsers: numberOf(consentOverview, 'totalVisitUsers', 'TotalVisitUsers'),
      acceptedConsentUsers: numberOf(consentOverview, 'acceptedConsentUsers', 'AcceptedConsentUsers'),
      cancelledConsentUsers: numberOf(consentOverview, 'cancelledConsentUsers', 'CancelledConsentUsers'),
    },
    usageFunnelByForm: arrayOf(valueOf(data, 'usageFunnelByForm', 'UsageFunnelByForm')).map(normalizeFunnelStage),
    accessChannelBreakdown: normalizePie(valueOf(data, 'accessChannelBreakdown', 'AccessChannelBreakdown')),
    newVsReturningUsers: normalizePie(valueOf(data, 'newVsReturningUsers', 'NewVsReturningUsers')),
    taxSavingsData: arrayOf(valueOf(data, 'taxSavingsData', 'TaxSavingsData')).map(normalizeTaxSavings),
    satisfactionData: normalizeSatisfaction(satisfactionData),
    productCtrData: arrayOf(valueOf(data, 'productCtrData', 'ProductCtrData')).map(normalizeProductCtr),
    customerIncomeData: arrayOf(valueOf(data, 'customerIncomeData', 'CustomerIncomeData')).map(normalizeCustomerIncome),
    dailyTrendData: arrayOf(valueOf(data, 'dailyTrendData', 'DailyTrendData')).map(normalizeDailyTrend),
    dataSourceIssues: arrayOf(valueOf(data, 'dataSourceIssues', 'DataSourceIssues')),
  };
};

const parseDashboardDateRange = (dateRangeText) => {
  const datePattern = /(\d{1,2})\/(\d{1,2})\/(\d{4})/g;
  const matches = [...String(dateRangeText || '').matchAll(datePattern)];
  const today = todayKey();

  if (matches.length === 0) {
    return { startDate: today, endDate: today };
  }

  const startDateStr = `${matches[0][1]}/${matches[0][2]}/${matches[0][3]}`;
  const endDateStr = matches[1]
    ? `${matches[1][1]}/${matches[1][2]}/${matches[1][3]}`
    : startDateStr;

  const startDate = parseDateString(startDateStr) || today;
  const endDate = parseDateString(endDateStr) || startDate;

  return { startDate, endDate };
};

export const getTaxDashboardData = async (dateRangeText) => {
  const { startDate, endDate } = parseDashboardDateRange(dateRangeText);
  const response = await http.get('api/v1/TaxDashboard/live', {
    params: { startDate, endDate },
  });

  return normalizeLiveData(unwrapResponseData(response));
};

export const getYearlyData = async () => {
  const response = await http.get('api/v1/TaxDashboard/yearly');
  const rows = arrayOf(unwrapResponseData(response));

  return rows.reduce((acc, row) => {
    const year = String(valueOf(row, 'year', 'Year') || '');
    if (!year) return acc;

    acc[year] = {
      pending: numberOf(row, 'pending', 'Pending'),
      completed: numberOf(row, 'completed', 'Completed'),
      productRecommendation: numberOf(row, 'productRecommendation', 'ProductRecommendation'),
    };

    return acc;
  }, {});
};
